import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { HotelPricingConfig, HotelPricingRates } from '@monorepo/functions/src/types/config'
import { format } from 'date-fns'
import {
    collection,
    deleteDoc,
    doc,
    onSnapshot,
    query,
    setDoc,
    where,
    type QueryConstraint,
} from 'firebase/firestore'

import { firestore } from '@/shared/firebase/firebase'
import { useAuth } from '@/shared/auth'
import {
    findCurrentPeriod,
    periodRatesToConfig,
    resolvePricingForDate,
} from '@/shared/utils/pricing-periods'

type PricingPeriodContextValue = {
    periods: HotelPricingRates[]
    loading: boolean
    currentPeriod: HotelPricingRates | null
    resolveConfigForDate: (date: string) => HotelPricingConfig
    addPeriod: (period: HotelPricingRates) => Promise<void>
    updatePeriod: (id: string, data: Partial<HotelPricingRates>) => Promise<void>
    removePeriod: (id: string) => Promise<void>
}

const PricingPeriodContext = createContext<PricingPeriodContextValue | null>(null)

const periodsCollection = () => collection(firestore, 'configs', 'hotel_pricing', 'periods')

type PricingPeriodProviderProps = {
    children: ReactNode
    activeOnly?: boolean
}

export function PricingPeriodProvider({ children, activeOnly = true }: PricingPeriodProviderProps) {
    const { isLoggedIn, isLoadingUser } = useAuth()
    const [periods, setPeriods] = useState<HotelPricingRates[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (isLoadingUser) {
            return
        }

        if (!isLoggedIn) {
            setPeriods([])
            setLoading(false)
            return
        }

        setLoading(true)
        const constraints: QueryConstraint[] = []
        if (activeOnly) {
            const today = format(new Date(), 'yyyy-MM-dd')
            constraints.push(where('endDate', '>=', today))
        }

        const q = constraints.length ? query(periodsCollection(), ...constraints) : query(periodsCollection())

        const unsubscribe = onSnapshot(
            q,
            snapshot => {
                const nextPeriods = snapshot.docs.map(
                    periodDoc => ({ id: periodDoc.id, ...periodDoc.data() }) as HotelPricingRates,
                )
                nextPeriods.sort((a, b) => a.startDate.localeCompare(b.startDate))
                setPeriods(nextPeriods)
                setLoading(false)
            },
            error => {
                console.error('Error loading pricing periods:', error)
                setLoading(false)
            },
        )

        return unsubscribe
    }, [activeOnly, isLoggedIn, isLoadingUser])

    const currentPeriod = useMemo(() => findCurrentPeriod(periods), [periods])

    const resolveConfigForDate = useCallback(
        (date: string) => resolvePricingForDate(date, periods),
        [periods],
    )

    const addPeriod = useCallback(async (period: HotelPricingRates) => {
        const { id, ...data } = period
        await setDoc(doc(periodsCollection(), id), data)
    }, [])

    const updatePeriod = useCallback(async (id: string, data: Partial<HotelPricingRates>) => {
        const periodRef = doc(periodsCollection(), id)
        const existing = periods.find(period => period.id === id)
        if (!existing) return
        const { id: _id, ...existingData } = existing
        const { id: _dataId, ...partialData } = data as HotelPricingRates
        await setDoc(periodRef, { ...existingData, ...partialData }, { merge: true })
    }, [periods])

    const removePeriod = useCallback(async (id: string) => {
        await deleteDoc(doc(periodsCollection(), id))
    }, [])

    const value = useMemo(
        () => ({
            periods,
            loading,
            currentPeriod,
            resolveConfigForDate,
            addPeriod,
            updatePeriod,
            removePeriod,
        }),
        [periods, loading, currentPeriod, resolveConfigForDate, addPeriod, updatePeriod, removePeriod],
    )

    return <PricingPeriodContext.Provider value={value}>{children}</PricingPeriodContext.Provider>
}

export function usePricingPeriods() {
    const context = useContext(PricingPeriodContext)
    if (!context) {
        throw new Error('usePricingPeriods must be used within a PricingPeriodProvider')
    }
    return context
}

export function usePricingPeriodsOptional() {
    return useContext(PricingPeriodContext)
}

export function useResolvedPricingConfig(referenceDate?: Date | string, periodId?: string) {
    const { periods, loading, currentPeriod, resolveConfigForDate } = usePricingPeriods()

    const config = useMemo(() => {
        if (periodId) {
            const period = periods.find(p => p.id === periodId)
            return period ? periodRatesToConfig(period) : null
        }

        const date =
            typeof referenceDate === 'string'
                ? referenceDate
                : referenceDate
                  ? format(referenceDate, 'yyyy-MM-dd')
                  : format(new Date(), 'yyyy-MM-dd')

        if (!periods.length) return null
        return resolveConfigForDate(date)
    }, [periodId, periods, referenceDate, resolveConfigForDate])

    const resolvedPeriod = useMemo(() => {
        if (periodId) return periods.find(p => p.id === periodId) ?? null
        return currentPeriod
    }, [periodId, periods, currentPeriod])

    return { config, periods, loading, currentPeriod: resolvedPeriod }
}
